import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import {
	Box,
	Button,
	Card,
	CardContent,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormLabel,
	IconButton,
	Radio,
	RadioGroup,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import type { Information } from "@/shared/lib/api/api-client";
import { apiClient } from "@/shared/lib/api/api-client";
import { informationSchema } from "@/shared/schemas/informationSchema";
import {
	convertApiDateTimeToLocal,
	convertDateTimeLocalToApi,
	formatDate,
	formatDateTimeForApi,
} from "@/shared/utils";
import { useImageUpload } from "../hooks";

const CLOUDFRONT_DOMAIN = import.meta.env.VITE_CLOUDFRONT_DOMAIN;

interface EditInformationDialogProps {
	open: boolean;
	info: Information | null;
	onClose: () => void;
	onSuccess: (message?: string) => void;
	onError: (message: string) => void;
}

export default function EditInformationDialog({
	open,
	info,
	onClose,
	onSuccess,
	onError,
}: EditInformationDialogProps): React.ReactElement {
	const bodyInputRef = useRef<HTMLTextAreaElement>(null);
	const { uploadedImages, handleImageUpload, removeUploadedImage, clearUploadedImages } =
		useImageUpload();
	const [formData, setFormData] = useState({
		title: "",
		body: "",
		filenameList: [] as string[],
	});
	const [loading, setLoading] = useState(false);
	const [existingImages, setExistingImages] = useState<Array<{ filename: string; url: string }>>(
		[],
	);
	const [errors, setErrors] = useState<{ title?: string; body?: string }>({});

	// 予約配信関連の状態
	const [deliveryType, setDeliveryType] = useState<"immediate" | "scheduled">("immediate");
	const [scheduledDateTime, setScheduledDateTime] = useState("");

	useEffect(() => {
		if (info) {
			// 既存の画像を抽出（{{filename:xxx}}形式）
			const filenameMatches = info.body.match(/\{\{filename:([^}]+)\}\}/g) || [];
			const existingImgsFromPlaceholder = filenameMatches.map((match) => {
				const filename = match.replace(/\{\{filename:(.+)\}\}/, "$1");
				return {
					filename,
					url: `https://${CLOUDFRONT_DOMAIN}/images/home/detail/${info.id}/${filename}`,
				};
			});

			const pathMatches = info.body.match(/\/images\/home\/detail\/[^\s"'<>]+/g) || [];
			const existingImgsFromPath = pathMatches.map((path) => {
				return {
					filename: path,
					url: `https://${CLOUDFRONT_DOMAIN}${path}`,
				};
			});

			const existingImgs = [...existingImgsFromPlaceholder, ...existingImgsFromPath];

			setFormData({
				title: info.title,
				body: info.body,
				filenameList: [],
			});
			setExistingImages(existingImgs);
			clearUploadedImages();

			// 日時が未来の場合は予約配信として扱う
			const infoDate = new Date(info.date);
			const now = new Date();
			if (infoDate > now) {
				setDeliveryType("scheduled");
				setScheduledDateTime(convertApiDateTimeToLocal(info.date));
			} else {
				setDeliveryType("immediate");
				setScheduledDateTime("");
			}
		}
	}, [info, clearUploadedImages]);

	const handleChange =
		(field: "title" | "body") =>
		(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = event.target.value;
			setFormData({
				...formData,
				[field]: value,
			});

			// リアルタイムバリデーション
			const result = informationSchema.shape[field].safeParse(value);
			if (!result.success) {
				setErrors((prev) => ({
					...prev,
					[field]: result.error.issues[0]?.message,
				}));
			} else {
				setErrors((prev) => {
					const newErrors = { ...prev };
					delete newErrors[field];
					return newErrors;
				});
			}
		};

	const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files || []);
		if (files.length === 0) return;

		const imgTags = handleImageUpload(files);

		// カーソル位置にimgタグを挿入
		if (bodyInputRef.current) {
			const cursorPosition = bodyInputRef.current.selectionStart || formData.body.length;
			const textBefore = formData.body.substring(0, cursorPosition);
			const textAfter = formData.body.substring(cursorPosition);

			const newBody = textBefore + imgTags + textAfter;

			setFormData({
				...formData,
				body: newBody,
			});

			// フォーカスを戻す
			setTimeout(() => {
				if (bodyInputRef.current) {
					const newCursorPosition = cursorPosition + imgTags.length;
					bodyInputRef.current.focus();
					bodyInputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
				}
			}, 0);
		}

		// inputをリセット
		e.target.value = "";
	};

	const handleRemoveImage = (imgId: string) => {
		removeUploadedImage(imgId);

		// 本文から該当する画像タグを削除
		const updatedBody = formData.body.replace(`<img src="{{image-${imgId}}}" />`, "");
		setFormData({
			...formData,
			body: updatedBody,
		});
	};

	const handleSubmit = async () => {
		if (!info) return;

		// バリデーション
		const validationResult = informationSchema.safeParse({
			title: formData.title,
			body: formData.body,
		});

		if (!validationResult.success) {
			const fieldErrors: { title?: string; body?: string } = {};
			for (const issue of validationResult.error.issues) {
				const field = issue.path[0] as "title" | "body";
				if (!fieldErrors[field]) {
					fieldErrors[field] = issue.message;
				}
			}
			setErrors(fieldErrors);
			return;
		}

		// 予約配信の場合は日時が必須
		if (deliveryType === "scheduled" && !scheduledDateTime) {
			onError("予約配信を選択した場合、配信日時を指定してください");
			return;
		}

		// 予約配信の日時が過去でないかチェック
		if (deliveryType === "scheduled" && scheduledDateTime) {
			const scheduledDate = new Date(scheduledDateTime);
			const now = new Date();
			if (scheduledDate <= now) {
				onError("配信日時は現在時刻より後の時間を指定してください");
				return;
			}
		}

		setLoading(true);
		try {
			// ファイル名のリストを生成
			const filenameList = uploadedImages.map((img) => img.file.name);

			// プレースホルダーをファイル名に変換したbodyを作成
			let processedBody = formData.body;
			for (const img of uploadedImages) {
				processedBody = processedBody.replace(
					`{{image-${img.id}}}`,
					`{{filename:${img.file.name}}}`,
				);
			}

			const dataToSubmit = {
				title: formData.title,
				body: processedBody,
				filenameList,
				date:
					deliveryType === "scheduled" && scheduledDateTime
						? convertDateTimeLocalToApi(scheduledDateTime)
						: formatDateTimeForApi(new Date()), // 即時配信の場合は現在時刻
			};

			const result = await apiClient.information.update(info.id, dataToSubmit);

			if (result.error) {
				onError(result.error);
				return;
			}

			// 署名付きURLを使って新しい画像をアップロード
			const presignedUrls =
				(result.data as any)?.presignedUrlToPutInformationImage ||
				(result.data as any)?.presignedUrlToPutInformationImageList ||
				[];
			if (presignedUrls.length > 0) {
				for (let i = 0; i < uploadedImages.length; i++) {
					const { file } = uploadedImages[i];
					// オブジェクト形式とstring形式の両方に対応
					const presignedUrl =
						typeof presignedUrls[i] === "string" ? presignedUrls[i] : presignedUrls[i]?.signedUrl;
					if (presignedUrl) {
						try {
							const uploadResponse = await fetch(presignedUrl, {
								method: "PUT",
								body: file,
							});

							if (!uploadResponse.ok) {
								throw new Error(`Failed to upload ${file.name}`);
							}
						} catch (_error) {
							onError(`画像 ${file.name} のアップロードに失敗しました`);
							return;
						}
					}
				}
			}

			// 配信タイプに応じたメッセージで成功通知
			const successMessage =
				deliveryType === "scheduled" ? "お知らせの予約を更新しました" : "お知らせを更新しました";
			onSuccess(successMessage);
			onClose();
		} catch (error) {
			onError(error instanceof Error ? error.message : "更新に失敗しました");
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		if (!loading) {
			onClose();
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="md"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: 2,
				},
			}}
		>
			<DialogTitle sx={{ pb: 1.5 }}>
				<Stack direction="row" alignItems="center" justifyContent="space-between">
					<Typography variant="h6" component="div">
						お知らせを編集
					</Typography>
					<IconButton
						aria-label="close"
						onClick={handleClose}
						size="small"
						disabled={loading}
						sx={{
							color: "grey.600",
							"&:hover": {
								backgroundColor: "grey.100",
							},
						}}
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Stack>
			</DialogTitle>
			<DialogContent sx={{ px: 3, pb: 2 }}>
				<Stack spacing={3} sx={{ mt: 1 }}>
					<TextField
						label="タイトル"
						required
						fullWidth
						value={formData.title}
						onChange={handleChange("title")}
						disabled={loading}
						placeholder="お知らせのタイトルを入力してください"
						error={!!errors.title}
						helperText={errors.title}
					/>

					{/* 本文入力 */}
					<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
						<Tooltip title="画像を追加" placement="left">
							<IconButton component="label" color="primary" size="medium" disabled={loading}>
								<AttachFileIcon />
								<input type="file" hidden accept="image/*" multiple onChange={handleImageSelect} />
							</IconButton>
						</Tooltip>
						<TextField
							label="本文HTML"
							required
							fullWidth
							multiline
							rows={6}
							value={formData.body}
							onChange={handleChange("body")}
							disabled={loading}
							placeholder="お知らせの内容を入力してください"
							inputRef={bodyInputRef}
							error={!!errors.body}
							helperText={errors.body || `${formData.body.length}/20000 文字`}
						/>
					</Box>

					{/* 配信設定 */}
					<Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 2 }}>
						<FormControl component="fieldset">
							<FormLabel component="legend" sx={{ mb: 1, fontWeight: "bold" }}>
								配信設定
							</FormLabel>
							<RadioGroup
								value={deliveryType}
								onChange={(e) => setDeliveryType(e.target.value as "immediate" | "scheduled")}
							>
								<FormControlLabel
									value="immediate"
									control={<Radio />}
									label="即時配信"
									sx={{ mb: 1 }}
								/>
								<FormControlLabel
									value="scheduled"
									control={<Radio />}
									label="予約配信"
									sx={{ mb: 1 }}
								/>
							</RadioGroup>

							{deliveryType === "scheduled" && (
								<Box sx={{ mt: 2 }}>
									<TextField
										label="配信日時"
										type="datetime-local"
										value={scheduledDateTime}
										onChange={(e) => setScheduledDateTime(e.target.value)}
										fullWidth
										required
										disabled={loading}
										InputLabelProps={{
											shrink: true,
										}}
										inputProps={{
											min: new Date().toISOString().slice(0, 16),
										}}
										helperText="指定した日時に自動で配信されます"
									/>
								</Box>
							)}
						</FormControl>
					</Box>

					{/* アップロード画像 */}
					{uploadedImages.length > 0 && (
						<Box>
							<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
								追加する画像
							</Typography>
							<Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
								{uploadedImages.map((img) => (
									<Box
										key={img.id}
										sx={{
											position: "relative",
											width: 120,
											height: 120,
											borderRadius: 1,
											overflow: "hidden",
											border: "1px solid #e0e0e0",
										}}
									>
										<img
											src={img.preview}
											alt={`upload-${img.id}`}
											style={{
												width: "100%",
												height: "100%",
												objectFit: "cover",
											}}
										/>
										<IconButton
											size="small"
											onClick={() => handleRemoveImage(img.id)}
											sx={{
												position: "absolute",
												top: 4,
												right: 4,
												backgroundColor: "rgba(255, 255, 255, 0.9)",
												"&:hover": {
													backgroundColor: "rgba(255, 255, 255, 1)",
												},
											}}
										>
											<DeleteIcon fontSize="small" />
										</IconButton>
									</Box>
								))}
							</Box>
						</Box>
					)}

					{/* 既存画像 */}
					{existingImages.length > 0 && (
						<Box>
							<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
								既存の画像
							</Typography>
							<Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
								{existingImages.map((img) => (
									<Box
										key={img.filename}
										sx={{
											width: 120,
											height: 120,
											borderRadius: 1,
											overflow: "hidden",
											border: "1px solid #e0e0e0",
										}}
									>
										<img
											src={img.url}
											alt={img.filename}
											style={{
												width: "100%",
												height: "100%",
												objectFit: "cover",
											}}
										/>
									</Box>
								))}
							</Box>
						</Box>
					)}

					{/* プレビュー */}
					<Box>
						<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
							プレビュー
						</Typography>
						<Box
							sx={{
								backgroundColor: "#ff5f49",
								p: 3,
								borderRadius: 2,
								display: "flex",
								justifyContent: "center",
							}}
						>
							<Card
								sx={{
									backgroundColor: "#FFFFFF",
									color: "#000000",
									boxShadow: 2,
									borderRadius: 3,
									width: "100%",
									maxWidth: 500,
								}}
							>
								<CardContent sx={{ p: 0 }}>
									<Box sx={{ px: 3, pt: 2.5, pb: 3 }}>
										{/* 日付 */}
										<Typography
											sx={{
												color: "#8c8c8c",
												fontSize: "12px",
												mb: 0.5,
												pl: 1,
											}}
										>
											{info?.date ? formatDate(info.date) : formatDate(new Date())}
										</Typography>

										{/* タイトル */}
										<Typography
											sx={{
												fontSize: "18px",
												fontWeight: "bold",
												mb: 2,
												pl: 1,
												color: "#ff5f49",
											}}
										>
											{formData.title || "タイトル"}
										</Typography>

										{/* ボーダー */}
										<Box
											sx={{
												height: "1px",
												backgroundColor: "#e0e0e0",
												mb: 3,
											}}
										/>

										{/* 本文 */}
										<Box
											sx={{
												pl: 1,
												"& p": {
													margin: 0,
													marginBottom: "1em",
													fontWeight: "medium",
													wordBreak: "break-word",
													overflowWrap: "break-word",
												},
												"& a": {
													textDecoration: "underline",
													color: "#000000",
												},
												"& img": {
													width: "80%",
													display: "block",
													margin: "auto",
												},
												"& ul": {
													paddingLeft: "1em",
													"& li": {
														paddingLeft: "1em",
														listStyle: "none",
														position: "relative",
														"&::before": {
															content: '"・"',
															marginLeft: "-1em",
															position: "absolute",
															left: 0,
														},
													},
												},
											}}
										>
											<Box
												sx={{
													fontSize: "14px",
													lineHeight: 1.8,
													wordBreak: "break-word",
													overflowWrap: "break-word",
												}}
												dangerouslySetInnerHTML={{
													__html: (() => {
														let body = formData.body || "本文HTML";
														// 新規アップロード画像のプレースホルダーをプレビューURLに置換
														for (const img of uploadedImages) {
															body = body.replace(`{{image-${img.id}}}`, img.preview);
														}
														// 既存画像のプレースホルダーをCloudFront URLに置換
														for (const img of existingImages) {
															if (img.filename.startsWith("/images/")) {
																body = body.replace(
																	new RegExp(
																		img.filename.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
																		"g",
																	),
																	img.url,
																);
															} else {
																body = body.replace(`{{filename:${img.filename}}}`, img.url);
															}
														}
														return body;
													})(),
												}}
											/>
										</Box>
									</Box>
								</CardContent>
							</Card>
						</Box>
					</Box>
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
				<Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", width: "100%" }}>
					<Button
						onClick={handleClose}
						variant="outlined"
						color="inherit"
						disabled={loading}
						sx={{ minWidth: 120 }}
					>
						キャンセル
					</Button>
					<Button
						onClick={handleSubmit}
						variant="contained"
						color="primary"
						disabled={loading}
						sx={{ minWidth: 120 }}
					>
						{loading
							? deliveryType === "scheduled"
								? "予約更新中..."
								: "更新中..."
							: deliveryType === "scheduled"
								? "予約更新"
								: "更新"}
					</Button>
				</Box>
			</DialogActions>
		</Dialog>
	);
}
